import * as yup from 'yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useGoogleSignupWithCredentialsForAdmin } from '../../../firebase/hooks';
import { useLocation, useNavigate } from 'react-router-dom';
import queryString from 'query-string';
import { AUTH_LOGIN } from 'utilities/routes';

const lettersAndSpacesOnly = /^[a-zA-Z\s]+$/;

export const FIRST_NAME_MAX_LENGTH = 30;
export const LAST_NAME_MAX_LENGTH = 30;
const PASSWORD_MIN_LENGTH = 8;
const PASSWORD_MAX_LENGTH = 64;

const passwordErrorMessage =
  'Password must contain at least 1 capital letter, 1 lowercase letter, 1 number, 1 special character, and be between 8 and 64 characters long';

export const useLoginAdminAccount = () => {
  const { search } = useLocation();
  const navigate = useNavigate();
  const { token, email } = queryString.parse(search);

  const validationSchema = yup.object().shape({
    firstName: yup
      .string()
      .required('This field is required')
      .matches(
        lettersAndSpacesOnly,
        'This field must contain only letters and spaces',
      )
      .max(
        FIRST_NAME_MAX_LENGTH,
        `Max symbol limit is ${FIRST_NAME_MAX_LENGTH}`,
      ),

    lastName: yup
      .string()
      .required('This field is required')
      .matches(
        lettersAndSpacesOnly,
        'This field must contain only letters and spaces',
      )
      .max(LAST_NAME_MAX_LENGTH, `Max symbol limit is ${LAST_NAME_MAX_LENGTH}`),

    password: yup
      .string()
      .required('This field is required')
      // Complexity checks combined into one message as requested
      .min(PASSWORD_MIN_LENGTH, passwordErrorMessage)
      .max(PASSWORD_MAX_LENGTH, passwordErrorMessage)
      .matches(/[a-z]/, passwordErrorMessage)
      .matches(/[A-Z]/, passwordErrorMessage)
      .matches(/\d/, passwordErrorMessage)
      .matches(/[!@#$%^&*(),.?":{}|<>_]/, passwordErrorMessage),

    confirmPassword: yup
      .string()
      .required('This field is required')
      .oneOf([yup.ref('password')], 'Password doesn`t match'),
  });

  const { handleGoogleSignupWithCredentials, loading } =
    useGoogleSignupWithCredentialsForAdmin();

  const handleSignup = (data: yup.InferType<typeof validationSchema>) => {
    return handleGoogleSignupWithCredentials(
      email as string,
      data.password,
      token as string,
      data.firstName,
      data.lastName,
    ).then(() => navigate(`${AUTH_LOGIN}?verifiedAdmin=true`));
  };

  const methods = useForm({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    resolver: yupResolver(validationSchema),
  });

  return {
    methods,
    loading,
    onSubmit: methods.handleSubmit(handleSignup),
  };
};
